/* eslint-disable indent */
import { saveAs } from 'file-saver';
import { tap } from 'rxjs/operators';
import { CheckboxFilterComponent } from '@//shared/components/table/components/checkbox-filter/checkbox-filter.component';
import { SearchbarComponent } from '@/shared/components/table/components/searchbar/searchbar.component';
import { FilterComponent } from '@/shared/components/table/components/filter/filter.component';
import { PaginationMetadata, TableDataSource } from '@/shared/components/table/table.model';
import { finalize } from 'rxjs/operators';
import { SortDirection, UserListFiltersInput, UsersFragment, UsersGQL, UserSortDefinitionInput, UserSortField } from '@/core/graphql/graphql';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CollectionViewer } from '@angular/cdk/collections';
import { PaginatorComponent } from '@/shared/components/table/components/paginator/paginator.component';
import { MatSort, Sort } from '@angular/material/sort';
import { USER_FILTER_CONFIG, USER_SORT_FIELD } from '@/modules/users/users.const';

const DEFAULT_PAGINATION_FACTORY = () => ({
  previousCursors: [],
  currentCursor: null,
  nextCursor: null,
  pageSize: 20,
  count: null,
});

export class UserDataSource extends TableDataSource<UsersFragment> {
  public readonly _loading$: Observable<boolean>;
  public readonly _downloadingCSVSubject = new BehaviorSubject<boolean>(false);
  protected _paginator: PaginatorComponent;
  protected _sort: MatSort;
  protected _filter: FilterComponent;
  protected _searchbar: SearchbarComponent;
  private _currentPagination: PaginationMetadata = DEFAULT_PAGINATION_FACTORY();
  private _sortDefinition: UserSortDefinitionInput = {
    sortField: UserSortField.CreatedAt,
    sortDirection: SortDirection.Desc,
  };
  private _filters: UserListFiltersInput = {
    isPremium: undefined,
    searchTerm: '',
  };
  private readonly _usersSubject = new BehaviorSubject<UsersFragment[]>([]);
  private readonly _loadingSubject = new BehaviorSubject<boolean>(true);
  private readonly _dataChangedSubject = new Subject<void>();
  private readonly _paginationMetaSubject = new BehaviorSubject<PaginationMetadata>(this._currentPagination);

  public constructor (
    private readonly _usersGql: UsersGQL,
  ) {
    super();
    this._loading$ = this._loadingSubject.asObservable();
    this.dataChanged$ = this._dataChangedSubject.asObservable();
  }

  public set paginator(component: PaginatorComponent) {
    this._paginator = component;
    this._paginator._pageSizeOptions = [20, 100, 500, 1000, 2000, 5000];
    this._paginator.fetchedPageCursor$.subscribe(this.loadData.bind(this));
  }

  public set sort(direction: MatSort) {
    this._sort = direction;
    this._sort.sortChange.subscribe(this.onSortChange.bind(this));
  }

  public set filter(component: FilterComponent) {
    this._filter = component;
    this._filter.selectedFilter$.subscribe(this.onFilterChange.bind(this));
    this._filter.setFilterConfig(USER_FILTER_CONFIG);
  }

  public set searchbar(component: SearchbarComponent) {
    this._searchbar = component;
    this._searchbar.appliedSearchTerm$.subscribe((x) => this.onSearchSubmission(x));
  }

  public set checkboxFilter(component: CheckboxFilterComponent) {
      throw new Error('Method not implemented');
  }

  public connect(_: CollectionViewer): Observable<readonly UsersFragment[]> {
    return this._usersSubject.asObservable().pipe(
      tap(_ => this._dataChangedSubject.next()),
    );
  }

  public disconnect(_: CollectionViewer): void {
    this._usersSubject.complete();
    this._loadingSubject.complete();
    this._downloadingCSVSubject.complete();
  }

  public loadData(currentPageCursor: string | null | undefined): void {
    this._loadingSubject.next(true);
    this._usersGql
      .fetch({
        filters: this._filters,
        meta: {
          pageSize: this._currentPagination.pageSize,
          pageCursor: currentPageCursor,
        },
        sort: [this._sortDefinition],
      }).pipe(
        finalize(() => this._loadingSubject.next(false)),
      ).subscribe((x) => {
        this._usersSubject.next(x.data.users.items);
        this._currentPagination.nextCursor = x.data.users.meta.nextPageCursor;
        this._currentPagination.count = x.data.users.meta.count;
        this._paginationMetaSubject.next(this._currentPagination);
        this._paginator.getDatasourceMetaData(this._paginationMetaSubject);
      });
  }

  public onSortChange({ active, direction }: Sort): void {
    if (direction) {
      this._sortDefinition = {
        sortField: USER_SORT_FIELD(active),
        sortDirection: direction === 'asc' ? SortDirection.Asc : SortDirection.Desc,
      };
    } else {
      this._sortDefinition = {
        sortField: UserSortField.CreatedAt,
        sortDirection: SortDirection.Desc,
      };
    }
    this.resetDatasourcePagination();
  }

  public onFilterChange(selection: string): void {
    switch (selection) {
      case 'All':
        this._filters.isPremium = undefined;
        break;
      case 'Premium':
        this._filters.isPremium = true;
        break;
      case 'Non-premium':
        this._filters.isPremium = false;
        break;
      default: 'All';
        break;
    }
    this.resetDatasourcePagination();
  }

  public onSearchSubmission(search: string): void {
    if (search.length > 0) {
      this._filters.searchTerm = search;
    } else {
      this._filters.searchTerm = undefined;
    }
    this.resetDatasourcePagination();
  }

  public async getFilteredUserList(): Promise<void> {
    this.toggleDownloadScreen();
    await this.getUserList(this._filters);
    this.toggleDownloadScreen();
  }

  public async getUnfilteredUserList(): Promise<void> {
    this.toggleDownloadScreen();
    await this.getUserList(null);
    this.toggleDownloadScreen();
  }

  private resetDatasourcePagination(): void {
    this._currentPagination = DEFAULT_PAGINATION_FACTORY();
    this._paginationMetaSubject.next(this._currentPagination);
    this._paginator.resetPaginator();
    this.loadData(null);
  }

  private async getUserList(filter: UserListFiltersInput | null): Promise<void> {
    const data: string[] = [];
    data.push("external_ID, Name, Username, Email, Registered, Premium, #ofClusers, #ofElements, #ofConnections, #ofFollowers, #ofFollowing\n");

    let pageCursor: any = "0";
    while (pageCursor) {
      const result = await this._usersGql.fetch({
        meta: {
          pageCursor: pageCursor,
          pageSize: this._currentPagination.count,
        },
        filters: filter,
        sort: [this._sortDefinition],
      }).toPromise();

      pageCursor = result.data.users.meta.nextPageCursor;
      result.data.users.items.forEach(x => {
        const line = x.id + "," + x.name + "," + x.username + "," + x.email + "," + x.createdAt + "," + x.isPremium + "," + x.statistics.numberOfClusters +
          "," + x.statistics.numberOfElements + "," + x.statistics.numberOfConnections + "," + x.statistics.numberOfFollowers + "," + x.statistics.numberOfFollowing + "\n";
        data.push(line);
      });
    }

    const dataToExport = new Blob(data, {
      type: "text/csv;charset=utf-8",
    });
    const today = new Date().toISOString().substring(0,10);
    saveAs(dataToExport, `users_${today}.csv`);
  }

  private toggleDownloadScreen(): void {
    this._loadingSubject.next(!this._downloadingCSVSubject.getValue());
    this._downloadingCSVSubject.next(!this._downloadingCSVSubject.getValue());
  }
}
